import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { isToday, parseISO } from 'date-fns';
import { Grid } from '@material-ui/core';
import groupBy from 'lodash-es/groupBy';
import uniq from 'lodash-es/uniq';

import useSession from 'core/hooks/use-session';
import { Card } from 'core/components/card';
import CollapsibleCard from 'core/components/collapsible-card';
import { Label } from 'core/components/label';
import { Loading } from 'core/components/loading';
import Section from 'core/components/section';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import { ApplicationLinks } from './components/application-url-form';
import { Task } from './components/task';
import { InternalTask } from './components/internal-task';
import completedIcon from 'customer/assets/completed.svg';
import clock from 'core/assets/svg/clock-filled.svg';
import coffeeIcon from './assets/coffee.svg';
import { InventoryImages } from './components/inventory-images';
import { TaskFilters } from './components/task-filters';
import {
  TaskStatus,
  getInternalTaskStatus,
  getPolicyTypeName,
  getPolicyType,
  getTaskStatus,
  sortTasksByPolicy
} from './task-helpers';
import useStyles from './onboarding-task-center.styles';

// How long a row keeps its status-changed highlight; outlasts the 1.6s animation in task.styles.js.
const STATUS_CHANGED_HIGHLIGHT_MS = 2000;

// Header count excludes internal tasks (per Figma spec).
const TaskStatusCard = ({
  title,
  tasks,
  internalTasks,
  open,
  onChange,
  statusChangedTaskId,
  onStatusChange,
  className,
  shaded = false,
  readOnly = false
}) => {
  const classes = useStyles();
  return (
    <CollapsibleCard
      showChevron={tasks.length > 0 || internalTasks.length > 0}
      className={classNames(classes.collapsible, { [classes.shaded]: shaded }, className)}
      open={open}
      onChange={onChange}
      content={
        <>
          <Label type="infoSubtitle" className={classes.greenLabel}>
            {title}
          </Label>
          <Label type="noteSmall" className={classes.greenLabel}>
            {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'} {title.toLowerCase()}
          </Label>
        </>
      }
      type="noBorders"
    >
      {tasks.map((task) => (
        <Task
          key={task.taskId}
          {...task}
          readOnly={readOnly}
          statusChanged={task.taskId === statusChangedTaskId}
          onStatusChange={onStatusChange}
        />
      ))}
      {internalTasks.map((task) => (
        <InternalTask
          key={task.taskId}
          {...task}
          statusChanged={task.taskId === statusChangedTaskId}
          onStatusChange={onStatusChange}
        />
      ))}
    </CollapsibleCard>
  );
};

const taskStatuses = [
  { status: TaskStatus.Incomplete, title: 'Incomplete' },
  { status: TaskStatus.Submitted, title: 'Submitted' },
  { status: TaskStatus.Approved, title: 'Approved' }
];

const OnboardingTaskCenter = () => {
  const classes = useStyles();
  const { canEditPolicies, canResetOnboardingTasks, canUploadTaskCenterDocuments, canViewInternalTasks, isAgency } =
    useSession();
  const {
    account,
    account: {
      accountLoading,
      id: accountId,
      fname,
      lname,
      tasks,
      incompleteTasks,
      completedTasks,
      tasksError,
      created,
      internalTasks,
      pendingInternalTasks
    }
  } = useStore();
  const toast = useToast();

  // Filters are only offered to users who can approve tasks (#35088).
  const showFilters = canResetOnboardingTasks;
  const [hiddenPolicyTypes, setHiddenPolicyTypes] = useState([]);
  const [collapsedStatuses, setCollapsedStatuses] = useState([]);
  // The row whose status the user just changed, so it can mark its arrival in its new status card.
  const [statusChangedTaskId, setStatusChangedTaskId] = useState(null);
  useEffect(() => {
    if (!statusChangedTaskId) {
      return undefined;
    }
    const timer = setTimeout(() => setStatusChangedTaskId(null), STATUS_CHANGED_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [statusChangedTaskId]);
  const togglePolicyType = (policyType) =>
    setHiddenPolicyTypes((hidden) =>
      hidden.includes(policyType) ? hidden.filter((item) => item !== policyType) : [...hidden, policyType]
    );
  const resetFilters = () => {
    setHiddenPolicyTypes([]);
    setCollapsedStatuses([]);
  };
  const setStatusCardOpen = (status, open) =>
    setCollapsedStatuses((collapsed) =>
      open ? collapsed.filter((item) => item !== status) : uniq([...collapsed, status])
    );

  const isTaskShown = (task) => !showFilters || !hiddenPolicyTypes.includes(getPolicyType(task.policyId));
  const filteredTasks = sortTasksByPolicy(tasks.filter(isTaskShown));
  const filteredInternalTasks = canViewInternalTasks ? sortTasksByPolicy(internalTasks.filter(isTaskShown)) : [];
  const tasksByStatus = groupBy(filteredTasks, getTaskStatus);
  const internalTasksByStatus = groupBy(filteredInternalTasks, getInternalTaskStatus);

  const policyTypeOptions = uniq(
    sortTasksByPolicy([...tasks, ...(canViewInternalTasks ? internalTasks : [])])
      .map((task) => getPolicyType(task.policyId))
      .filter(Boolean)
  ).map((policyType) => ({
    value: policyType,
    label: getPolicyTypeName(policyType),
    checked: !hiddenPolicyTypes.includes(policyType)
  }));
  const statusOptions = taskStatuses.map(({ status, title }) => ({
    value: status,
    label: title,
    checked: !collapsedStatuses.includes(status)
  }));

  useEffect(() => {
    if (tasksError) {
      toast.notify({
        type: 'error',
        message: tasksError
      });
    }
  }, [tasksError, toast]);

  // Fetch internal tasks separately — only for users with the permission
  useEffect(() => {
    if (canViewInternalTasks && accountId) {
      account.fetchInternalTasks(accountId);
    }
  }, [canViewInternalTasks, accountId, account]);

  const boundToday = created ? isToday(parseISO(created)) : false;

  // Appcues teaching bubble for task statuses (#24215)
  useEffect(() => {
    // Only show for IAs (not agencies) and when there are review pending tasks
    if (!isAgency && completedTasks.length > 0) {
      const hasReviewPendingTasks = completedTasks.some((task) => task.completedDate && !task.approvalDate);

      if (hasReviewPendingTasks) {
        // Show the teaching bubble with low priority (after other bubbles)
        setTimeout(() => {
          window?.Appcues?.show('24215');
        }, 1000); // Small delay to ensure other bubbles show first
      }
    }
  }, [isAgency, completedTasks]);

  // Internal tasks do NOT count toward the submitted tasks count (per Figma spec)
  const hasAnyTasks = tasks.length > 0 || (canViewInternalTasks && internalTasks.length > 0);

  // Summary banner reflects overall task progress (#31628). Regular tasks have no
  // separate "approved" bucket — approval is just `approvalDate` on a completed task.
  const totalTaskCount = tasks.length;
  const submittedTaskCount = completedTasks.length;
  const approvedTaskCount = completedTasks.filter((task) => !!task.approvalDate).length;

  // Icon matches the least-complete status: gray clock unless every task is approved,
  // in which case the green checkmark. Banner only shows once at least one task is submitted.
  let summaryIcon = clock;
  let summaryIconAlt = 'Clock Icon';
  let summaryMessage = null;
  if (submittedTaskCount > 0) {
    if (submittedTaskCount < totalTaskCount) {
      summaryMessage = `${submittedTaskCount}/${totalTaskCount} submitted for ${fname} ${lname}`;
    } else if (approvedTaskCount < totalTaskCount) {
      summaryMessage = `All of ${fname} ${lname}'s tasks have been submitted. Underwriting review in progress`;
    } else if (!canViewInternalTasks || pendingInternalTasks.length === 0) {
      // Suppress the "all completed" line when a permissioned staff user still has
      // pending internal tasks to review — it would otherwise sit above unfinished work.
      summaryIcon = completedIcon;
      summaryIconAlt = 'Rounded checkmark';
      summaryMessage = `All of ${fname} ${lname}'s tasks have been completed`;
    }
  }

  return (
    <Section
      title="Task Center"
      rightLabel="Total"
      rightValue={tasks.length ? `${accountLoading ? '...' : incompleteTasks.length} incomplete tasks` : null}
    >
      {accountLoading ? (
        <Loading />
      ) : (
        <div className={classes.sectionContent}>
          {canEditPolicies && <ApplicationLinks />}
          <InventoryImages />

          {!hasAnyTasks && boundToday && (
            <Card className={classes.container}>
              <Grid container justifyContent="center" alignItems="center" direction="column">
                <img className={classes.icon} src={clock} alt="Clock Icon" />
                <Label type="darkGreenMedium" className={classes.label}>
                  Onboarding tasks are generating
                </Label>
                <Label type="greenExtraSmall">Try refreshing the page to see if they're ready.</Label>
              </Grid>
            </Card>
          )}

          {!hasAnyTasks && !boundToday && (
            <Card>
              <div className={classes.empty}>
                <img className={classes.icon} src={boundToday ? clock : coffeeIcon} alt="Icon" />
                <Label className={classes.greenLabel}>
                  {boundToday
                    ? 'Onboarding tasks generating, please check back soon.'
                    : 'Relax, there are no tasks to complete'}
                </Label>
              </div>
            </Card>
          )}
          {showFilters && hasAnyTasks && (
            <TaskFilters
              policyTypeOptions={policyTypeOptions}
              statusOptions={statusOptions}
              onTogglePolicyType={togglePolicyType}
              onToggleStatus={(status) => setStatusCardOpen(status, collapsedStatuses.includes(status))}
              onReset={resetFilters}
            />
          )}
          {hasAnyTasks && (
            <Card style={{ overflow: 'visible' }}>
              {summaryMessage && (
                <div className={classes.container}>
                  <img src={summaryIcon} alt={summaryIconAlt} />
                  <Label type="darkGreenSmall">{summaryMessage}</Label>
                </div>
              )}
              {taskStatuses.map(({ status, title }, index) => (
                <TaskStatusCard
                  key={status}
                  title={title}
                  className={index === taskStatuses.length - 1 ? classes.lastStatusCard : undefined}
                  open={!collapsedStatuses.includes(status)}
                  onChange={(open) => setStatusCardOpen(status, open)}
                  shaded={status !== TaskStatus.Incomplete}
                  tasks={tasksByStatus[status] || []}
                  internalTasks={internalTasksByStatus[status] || []}
                  readOnly={status === TaskStatus.Incomplete && !canUploadTaskCenterDocuments}
                  statusChangedTaskId={statusChangedTaskId}
                  onStatusChange={setStatusChangedTaskId}
                />
              ))}
            </Card>
          )}
        </div>
      )}
    </Section>
  );
};

export default observer(OnboardingTaskCenter);
